import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import DonationReceiptPopup from "../component/DonationReceiptPopup";
import useHomeInteractions from "../component/useHomeInteractions";
import { getApiUrl } from "../api";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDonationSummary = () => {
  try {
    const stored = sessionStorage.getItem("lastDonation");
    const donation = stored ? JSON.parse(stored) : {};
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const transactionid = params.get("transactionid");
    const message = params.get("message");

    return {
      ...donation,
      status: status || donation.status,
      transactionid: transactionid || donation.transactionid,
      response_message: message || donation.response_message,
    };
  } catch {
    return {};
  }
};

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
  const timeout = window.setTimeout(resolve, milliseconds);
  signal.addEventListener("abort", () => {
    window.clearTimeout(timeout);
    reject(new DOMException("Request aborted.", "AbortError"));
  }, { once: true });
});

export default function AfterPayment() {
  const interactions = useHomeInteractions();
  const [monthlyType, setMonthlyType] = useState("monthly");
  const [showMonthlyPopup, setShowMonthlyPopup] = useState(false);
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const donation = useMemo(getDonationSummary, []);
  const amount = donation.amount || "";
  const donationTypeLabel = donation.donationTypeLabel || "";
  const donationTitle = donation.donationTitle || "";
  const transactionId = donation.transactionid || "";
  const paymentMethod = donation.paymentmethod || "";
  const paymentStatus = String(donation.status || "").toLowerCase();
  const isPaymentSuccess = paymentStatus === "success";
  const dateTime = formatDateTime(donation.dt);

  useEffect(() => {
    document.title = `${isPaymentSuccess ? "Payment Successful" : "Payment Status"} | Tarahum Charity Foundation`;
  }, [isPaymentSuccess]);

  useEffect(() => {
    if (!isPaymentSuccess || !transactionId) return undefined;

    const controller = new AbortController();
    const syncStorageKey = `businessCentralDonation:${transactionId}`;
    if (sessionStorage.getItem(syncStorageKey) === "posted") return undefined;

    const postSuccessfulDonation = async () => {
      const retryDelays = [0, 2000, 5000, 10000];
      let lastError;

      for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
        try {
          if (retryDelays[attempt]) await wait(retryDelays[attempt], controller.signal);
          const response = await fetch(getApiUrl("/api/PostSuccessfulDonationToBusinessCentral.ashx"), {
            method: "POST",
            credentials: "include",
            keepalive: true,
            signal: controller.signal,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ transactionId }),
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || !result.success) {
            throw new Error(result.diagnostic || result.message || "Unable to post the donation to Business Central.");
          }
          sessionStorage.setItem(syncStorageKey, "posted");
          return;
        } catch (error) {
          if (error.name === "AbortError") return;
          lastError = error;
        }
      }

      console.error("Business Central donation posting failed after automatic retries.", lastError);
    };

    postSuccessfulDonation();
    return () => controller.abort();
  }, [isPaymentSuccess, transactionId]);

  const handleShareDonation = async () => {
    const shareText = `I donated AED ${amount} to ${donationTitle} through Tarahum Charity Foundation. Transaction ID: ${transactionId}`;
    const shareUrl = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Tarahum Charity Foundation Donation",
          text: shareText,
          url: shareUrl,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        setShareMessage("Donation details copied to clipboard.");
        window.setTimeout(() => setShareMessage(""), 2500);
      } else {
        setShareMessage(shareText);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        setShareMessage("Unable to share donation details.");
        window.setTimeout(() => setShareMessage(""), 2500);
      }
    }
  };

  const handleBackHome = () => {
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new Event("app:navigate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Preloader />
      <Header />
      <LanguagePopup />
      <section>
        <div className="pay-afrer-wrap">
          <div className="container">
            <div className="after-payment-layout">
              <div className="success-left">
                <div style={{ textAlign: "center" }}>
                  <div className="icon-circle">
                    <img src={isPaymentSuccess ? "/images/img/tick.svg" : "/images/img/lock.svg"} alt="" />
                  </div>
                  <h1>{isPaymentSuccess ? "Thank You" : "Payment Not Completed"}</h1>
                  <p>{isPaymentSuccess ? "Your donation was successful" : `Your ${paymentMethod} transaction was not completed successfully.`}</p>
                  <p>{isPaymentSuccess ? "Your generosity will create a lasting impact and bring hope to those in need." : (donation.response_message || "Please try again or choose another payment method.")}</p>
                </div>

                <div className="amount-row">
                  <div className="amount-box">
                    <h3>{"Amount Donated"}</h3>
                    <p>{"AED "}{amount}</p>
                  </div>
                  <div className="amount-box">
                    <h3>{"Donation Type"}</h3>
                    <p>{donationTypeLabel}</p>
                  </div>
                  <div className="amount-box">
                    <h3>{"For"}</h3>
                    <p>{donationTitle}</p>
                  </div>
                </div>

                <div className="transaction-row">
                  <div className="transaction-box">
                    {"Transaction ID"}
                    <strong>{transactionId}</strong>
                  </div>
                  <div className="transaction-box">
                    {"Date & Time"}
                    <strong>{dateTime}</strong>
                  </div>
                  <div className="transaction-box">
                    {"Payment Method"}
                    <strong>{paymentMethod}</strong>
                  </div>
                </div>

                <div className="action-buttons">
                  <button className="secondary" type="button" onClick={() => setShowReceiptPopup(true)}>
                    <img src="/images/img/download-ic.svg" width="20" alt="" />
                    {" Download Receipt"}
                  </button>
                  <button className="secondary" type="button" onClick={handleShareDonation}>
                    <img src="/images/img/share-alt.svg" width="20" alt="" />
                    {" Share Your Donation"}
                  </button>
                  <button className="primary" type="button" onClick={handleBackHome}>
                    {"Back to Home"}
                  </button>
                </div>
                {shareMessage ? (
                  <div className="after-payment-message">
                    {shareMessage}
                  </div>
                ) : null}

                <div className="secure-box">
                  <img src="/images/img/lock.svg" width="28" alt="" />
                  {" 100% Secure & Encrypted. You will receive an email receipt shortly."}
                </div>
              </div>

              <div className="success-right">
                <div className="option-box">
                  <h3>{"What's Next?"}</h3>
                  <p>{"Would you like to make this a monthly donation? Your monthly support helps us plan ahead and reach more people in need."}</p>
                  <div className="option-buttons">
                    <button className={monthlyType === "monthly" ? "active" : ""} type="button" onClick={() => setMonthlyType("monthly")}>
                      <div className="ic-b-1">
                        <img src="/images/img/month-schedule.svg" alt="" />
                      </div>
                      <span>{"Monthly Giving"}</span>
                    </button>
                    <button className={monthlyType === "one-time" ? "active" : ""} type="button" onClick={() => setMonthlyType("one-time")}>
                      <div className="ic-b-1">
                        <img src="/images/img/donation-1.svg" alt="" />
                      </div>
                      <span>{"One-Time Donation"}</span>
                    </button>
                  </div>
                  <button type="button" onClick={() => setShowMonthlyPopup(true)} className="contnu-wrap">
                    {"Continue"}
                  </button>
                  <p style={{ textAlign: "center", color: "#5b6770", marginBottom: 0, marginTop: "10px" }}>
                    <a href="/" style={{ color: "#333" }}>{"Maybe later"}</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showMonthlyPopup ? (
        <div className="monthly-success-popup" style={{ display: "flex" }}>
          <div className="monthly-success-overlay" onClick={() => setShowMonthlyPopup(false)}></div>
          <div className="monthly-success-box">
            <div className="monthly-success-icon">{"Done"}</div>
            <h2>{"Monthly Giving Activated!"}</h2>
            <p>
              {"Thank you for joining our Monthly Giving Program."}
              <br />
              {"Your recurring donation will help provide continuous support to families and communities in need."}
            </p>
            <div className="monthly-success-details">
              <div className="monthly-detail-item">
                <span>{"Donation Type"}</span>
                <strong>{"Monthly Giving"}</strong>
              </div>
              <div className="monthly-detail-item">
                <span>{"Amount"}</span>
                <strong>{"AED "}{amount}{" / Month"}</strong>
              </div>
              <div className="monthly-detail-item">
                <span>{"Next Donation Date"}</span>
                <strong>{"01 June 2026"}</strong>
              </div>
            </div>
            <div className="monthly-success-message">
              {"Your kindness creates lasting impact every month."}
            </div>
            <div className="monthly-success-actions">
              <button className="monthly-btn primary-btn" type="button" onClick={() => setShowMonthlyPopup(false)}>
                {"Close"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showReceiptPopup ? (
        <DonationReceiptPopup donation={donation} onClose={() => setShowReceiptPopup(false)} />
      ) : null}

      <Footer />
      <FloatingWidgets {...interactions} />
    </>
  );
}
